<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { trans } from "laravel-vue-i18n";
import { FormInput, FormRadio, Submit } from "@/ts/components/form";
import { useForm } from "vee-validate";
import * as Yup from "yup";
import { useReCaptcha } from "vue-recaptcha-v3";
import { useErrors } from "@/ts/utils/api-functions";

const component = ref<HTMLDivElement>();

const emit = defineEmits(["submit", "back", "mounted"]);
const success = ref(false);
const loading = ref(false);
const reCaptcha = useReCaptcha();
const apiErrors = useErrors();

const schema = computed(() =>
    Yup.object({
        reason: Yup.string().oneOf(["reason_1", "reason_2", "reason_3", "reason_4"]),
        comment: Yup.string().optional(),
    }),
);

const { handleSubmit, meta } = useForm({
    initialValues: { reason: "", comment: "" },
    validationSchema: schema,
});

onMounted(() => {
    emit("mounted", component.value!.offsetHeight);

    onMounted(() => {
        window.addEventListener("resize", () => {
            emit("mounted", component.value!.offsetHeight);
        });

        window.addEventListener("orientationchange", () => {
            emit("mounted", component.value!.offsetHeight);
        });
    });
});

const onSubmit = handleSubmit(async (values, { resetForm }) => {
    loading.value = true;
    try {
        await reCaptcha!.recaptchaLoaded();
        const token = await reCaptcha!.executeRecaptcha("login");
        success.value = true;
        emit("submit", values);
    } catch (error) {
        apiErrors.parse(error);
    } finally {
        loading.value = false;
    }
});
</script>

<template>
    <div ref="component" class="w-full px-4 pt-10 pb-4 md:p-10 lg:p-[3.75rem]">
        <button class="group mb-4 inline-flex items-center text-14 uppercase !leading-normal" @click="emit('back')">
            <span class="mr-2 rotate-180 transform transition-transform group-hover:-translate-x-1">
                <svg-vue icon="arrow" class="h-4 w-auto"></svg-vue>
            </span>
            Terug
        </button>
        <h2 class="mb-6 font-heading text-32 font-[690] uppercase [font-stretch:_190%]">{{ trans("membership.cancel_reason_title") }}</h2>
        <form @submit="onSubmit" class="mt-10 flex flex-col gap-10">
            <div class="flex flex-col space-y-4">
                <p class="mb-2 text-14 uppercase !leading-normal">{{ trans("membership.cancel_reason_description") }}</p>
                <form-radio name="reason" on-value="reason_1" :placeholder="trans('membership.cancel_reason_option_1')" :label="trans('membership.cancel_reason_option_1')"></form-radio>
                <form-radio name="reason" on-value="reason_2" :placeholder="trans('membership.cancel_reason_option_2')" :label="trans('membership.cancel_reason_option_2')"></form-radio>
                <form-radio name="reason" on-value="reason_3" :placeholder="trans('membership.cancel_reason_option_3')" :label="trans('membership.cancel_reason_option_3')"></form-radio>
                <form-radio name="reason" on-value="reason_4" :placeholder="trans('membership.cancel_reason_option_4')" :label="trans('membership.cancel_reason_option_4')"></form-radio>
            </div>
            <form-input type="textarea" name="comment" :placeholder="trans('membership.cancel_reason_comment')" :label="trans('membership.cancel_reason_comment')"></form-input>
            <submit class="btn btn-black ml-auto w-full md:w-auto" :disabled="!meta.valid">
                {{ trans("membership.next") }}
                <svg-vue icon="arrow" class="ml-[.625rem] h-6 w-auto"></svg-vue>
            </submit>
        </form>
    </div>
</template>
